export const SUPPORTED_LOCALES = [
  'us',
  'ca',
  'gb',
  'fr',
  'it',
  'mx',
  'es',
  'br',
  'pt',
  'in',
  'jp'
] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export type RouteLanguage = 'en' | 'pt' | 'es' | 'fr' | 'it' | 'hi' | 'ja';

export const DEFAULT_LOCALE: AppLocale = 'us';

export const REGION_LANGUAGE: Record<AppLocale, RouteLanguage> = {
  us: 'en',
  ca: 'en',
  gb: 'en',
  fr: 'fr',
  it: 'it',
  mx: 'es',
  es: 'es',
  br: 'pt',
  pt: 'pt',
  in: 'hi',
  jp: 'ja'
};

export const REGION_HREFLANG: Record<AppLocale, string> = {
  us: 'en-US',
  ca: 'en-CA',
  gb: 'en-GB',
  fr: 'fr-FR',
  it: 'it-IT',
  mx: 'es-MX',
  es: 'es-ES',
  br: 'pt-BR',
  pt: 'pt-PT',
  in: 'hi-IN',
  jp: 'ja-JP'
};

type RouteConcept =
  | 'movie'
  | 'tv'
  | 'episode'
  | 'person'
  | 'movies'
  | 'tvShows'
  | 'genres'
  | 'inTheaters'
  | 'trending'
  | 'popular'
  | 'lists'
  | 'users'
  | 'watchList'
  | 'providers'
  | 'all'
  | 'tvTimeAlternative';

const ROUTE_SEGMENTS: Record<RouteLanguage, Record<RouteConcept, string>> = {
  en: {
    movie: 'movie',
    tv: 'tv',
    episode: 'episode',
    person: 'person',
    movies: 'movies',
    tvShows: 'tv-shows',
    genres: 'genres',
    inTheaters: 'in-theaters',
    trending: 'trending',
    popular: 'popular',
    lists: 'lists',
    users: 'users',
    watchList: 'watch-list',
    providers: 'where-to-watch',
    all: 'all',
    tvTimeAlternative: 'tv-time-alternative'
  },
  pt: {
    movie: 'filme',
    tv: 'serie',
    episode: 'episodio',
    person: 'pessoa',
    movies: 'filmes',
    tvShows: 'series',
    genres: 'generos',
    inTheaters: 'em-cartaz',
    trending: 'em-alta',
    popular: 'populares',
    lists: 'listas',
    users: 'usuarios',
    watchList: 'lista-para-assistir',
    providers: 'onde-assistir',
    all: 'todos',
    tvTimeAlternative: 'alternativa-tv-time'
  },
  es: {
    movie: 'pelicula',
    tv: 'serie',
    episode: 'episodio',
    person: 'persona',
    movies: 'peliculas',
    tvShows: 'series',
    genres: 'generos',
    inTheaters: 'en-cines',
    trending: 'tendencias',
    popular: 'populares',
    lists: 'listas',
    users: 'usuarios',
    watchList: 'lista-por-ver',
    providers: 'donde-ver',
    all: 'todos',
    tvTimeAlternative: 'alternativa-tv-time'
  },
  fr: {
    movie: 'film',
    tv: 'serie',
    episode: 'episode',
    person: 'personnalite',
    movies: 'films',
    tvShows: 'series',
    genres: 'genres',
    inTheaters: 'au-cinema',
    trending: 'tendances',
    popular: 'populaires',
    lists: 'listes',
    users: 'utilisateurs',
    watchList: 'liste-a-voir',
    providers: 'ou-regarder',
    all: 'tout',
    tvTimeAlternative: 'alternative-tv-time'
  },
  it: {
    movie: 'film',
    tv: 'serie',
    episode: 'episodio',
    person: 'persona',
    movies: 'film',
    tvShows: 'serie',
    genres: 'generi',
    inTheaters: 'al-cinema',
    trending: 'tendenze',
    popular: 'popolari',
    lists: 'liste',
    users: 'utenti',
    watchList: 'lista-da-guardare',
    providers: 'dove-guardare',
    all: 'tutti',
    tvTimeAlternative: 'alternativa-tv-time'
  },
  hi: {
    movie: 'film',
    tv: 'series',
    episode: 'episode',
    person: 'kalakar',
    movies: 'filmen',
    tvShows: 'series',
    genres: 'shailiyan',
    inTheaters: 'cinemagharon-mein',
    trending: 'trending',
    popular: 'lokpriya',
    lists: 'suchiyan',
    users: 'upayogakarta',
    watchList: 'dekhne-ki-suchi',
    providers: 'kahan-dekhen',
    all: 'sabhi',
    tvTimeAlternative: 'tv-time-ka-vikalp'
  },
  ja: {
    movie: 'eiga',
    tv: 'terebi',
    episode: 'episodo',
    person: 'jinbutsu',
    movies: 'eiga',
    tvShows: 'terebi-bangumi',
    genres: 'janru',
    inTheaters: 'kokai-chu',
    trending: 'torendo',
    popular: 'ninki',
    lists: 'risuto',
    users: 'yuza',
    watchList: 'mitai-risuto',
    providers: 'doko-de-miru',
    all: 'subete',
    tvTimeAlternative: 'tv-time-no-daitai'
  }
};

function localizedPattern(
  build: (segments: Record<RouteConcept, string>) => string
): Record<AppLocale, string> {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map(locale => [
      locale,
      build(ROUTE_SEGMENTS[REGION_LANGUAGE[locale]])
    ])
  ) as Record<AppLocale, string>;
}

export const pathnames = {
  '/': '/',
  '/news': {
    ...localizedPattern(() => '/news'),
    br: '/noticias',
    pt: '/noticias'
  },
  '/news/[slug]': {
    ...localizedPattern(() => '/news/[slug]'),
    br: '/noticias/[slug]',
    pt: '/noticias/[slug]'
  },
  '/movie/[id]': localizedPattern(s => `/${s.movie}/[id]`),
  '/tv/[id]': localizedPattern(s => `/${s.tv}/[id]`),
  '/episode/[id]': localizedPattern(s => `/${s.episode}/[id]`),
  '/person/[id]': localizedPattern(s => `/${s.person}/[id]`),
  '/person/[id]/movies': localizedPattern(s => `/${s.person}/[id]/${s.movies}`),
  '/person/[id]/tv-shows': localizedPattern(
    s => `/${s.person}/[id]/${s.tvShows}`
  ),
  '/genres/[id]': localizedPattern(s => `/${s.genres}/[id]`),
  '/genres/[id]/movies': localizedPattern(s => `/${s.genres}/[id]/${s.movies}`),
  '/genres/[id]/tv': localizedPattern(s => `/${s.genres}/[id]/${s.tv}`),
  '/in_theaters': localizedPattern(s => `/${s.inTheaters}`),
  '/trending': localizedPattern(s => `/${s.trending}`),
  '/popular': localizedPattern(s => `/${s.popular}`),
  '/lists/[id]': localizedPattern(s => `/${s.lists}/[id]`),
  '/users/[id]': localizedPattern(s => `/${s.users}/[id]`),
  '/users/[id]/lists': localizedPattern(s => `/${s.users}/[id]/${s.lists}`),
  '/users/[id]/watch-list/movies': localizedPattern(
    s => `/${s.users}/[id]/${s.watchList}/${s.movies}`
  ),
  '/users/[id]/watch-list/tvs': localizedPattern(
    s => `/${s.users}/[id]/${s.watchList}/${s.tvShows}`
  ),
  '/watch-providers/[slug]/all': localizedPattern(
    s => `/${s.providers}/[slug]/${s.all}`
  ),
  '/tv-time-alternative': localizedPattern(s => `/${s.tvTimeAlternative}`)
} as const;

export type LocalizedRoute = keyof typeof pathnames;
export type RouteParams = Record<string, string | number>;
export type RouteQuery =
  | URLSearchParams
  | Record<string, string | number | boolean | null | undefined>;

export function isAppLocale(locale: string): locale is AppLocale {
  return SUPPORTED_LOCALES.includes(locale as AppLocale);
}

function getExternalTemplate(route: LocalizedRoute, locale: AppLocale): string {
  const config = pathnames[route];

  return typeof config === 'string' ? config : config[locale];
}

function fillTemplate(template: string, params: RouteParams = {}): string {
  return template.replace(/\[([^\]]+)\]/g, (_, key: string) => {
    const value = params[key];

    if (value === undefined) {
      throw new Error(`Missing localized route parameter: ${key}`);
    }

    return encodeURIComponent(String(value));
  });
}

function serializeQuery(query?: RouteQuery): string {
  if (!query) return '';

  const searchParams =
    query instanceof URLSearchParams ? query : new URLSearchParams();

  if (!(query instanceof URLSearchParams)) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    });
  }

  const serialized = searchParams.toString();

  return serialized ? `?${serialized}` : '';
}

export function buildLocalizedPath({
  locale,
  route,
  params,
  query
}: {
  locale: AppLocale;
  route: LocalizedRoute;
  params?: RouteParams;
  query?: RouteQuery;
}): string {
  const pathname = fillTemplate(getExternalTemplate(route, locale), params);

  return `/${locale}${pathname === '/' ? '' : pathname}${serializeQuery(query)}`;
}

export function buildLocalizedUrl({
  baseUrl,
  ...pathOptions
}: Parameters<typeof buildLocalizedPath>[0] & { baseUrl: string }): string {
  return `${baseUrl.replace(/\/$/, '')}${buildLocalizedPath(pathOptions)}`;
}

function matchTemplate(template: string, pathname: string): RouteParams | null {
  const templateSegments = template.split('/').filter(Boolean);
  const pathnameSegments = pathname.split('/').filter(Boolean);

  if (templateSegments.length !== pathnameSegments.length) return null;

  const params: RouteParams = {};

  for (let index = 0; index < templateSegments.length; index += 1) {
    const templateSegment = templateSegments[index];
    const pathnameSegment = pathnameSegments[index];
    const parameterMatch = templateSegment.match(/^\[([^\]]+)\]$/);

    if (parameterMatch) {
      params[parameterMatch[1]] = decodeURIComponent(pathnameSegment);
    } else if (templateSegment !== pathnameSegment) {
      return null;
    }
  }

  return params;
}

const LOCALIZED_ROUTE_ENTRIES = (
  Object.keys(pathnames) as LocalizedRoute[]
).sort((a, b) => b.split('/').length - a.split('/').length);

const LEGACY_CAMPAIGN_ALIASES = ['/alternativa-tv-time', '/tvtime-alternative'];

export function getCanonicalLocalizedPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const locale = segments[0];

  if (!locale || !isAppLocale(locale)) return null;

  const unprefixedPathname = `/${segments.slice(1).join('/')}`;

  for (const route of LOCALIZED_ROUTE_ENTRIES) {
    const candidateTemplates = new Set<string>([
      route,
      ...SUPPORTED_LOCALES.map(candidateLocale =>
        getExternalTemplate(route, candidateLocale)
      )
    ]);

    if (route === '/tv-time-alternative') {
      LEGACY_CAMPAIGN_ALIASES.forEach(alias => candidateTemplates.add(alias));
    }

    for (const template of Array.from(candidateTemplates)) {
      const params = matchTemplate(template, unprefixedPathname);

      if (params) {
        const canonical = buildLocalizedPath({ locale, route, params });

        return canonical === pathname ? null : canonical;
      }
    }
  }

  return null;
}

export type TitleSlug = {
  slug: string;
  iso_3166_1: string;
};

export type TitleRouteHref = {
  pathname: '/movie/[id]' | '/tv/[id]';
  params: { id: string };
  query?: RouteQuery;
};

export function getTitleSlug(
  slugs: TitleSlug[] | undefined,
  locale: AppLocale
): TitleSlug | undefined {
  return (
    (slugs || []).find(slug => slug.iso_3166_1.toLowerCase() === locale) ||
    (slugs || []).find(
      slug => slug.iso_3166_1.toLowerCase() === DEFAULT_LOCALE
    ) ||
    slugs?.[0]
  );
}

export function buildTitleRouteHref({
  type,
  shortId,
  slugs,
  locale,
  query
}: {
  type: 'movie' | 'tv';
  shortId: string;
  slugs: TitleSlug[] | undefined;
  locale: AppLocale;
  query?: RouteQuery;
}): TitleRouteHref {
  const selectedSlug = getTitleSlug(slugs, locale)?.slug || 'no';

  return {
    pathname: type === 'movie' ? '/movie/[id]' : '/tv/[id]',
    params: { id: `${selectedSlug}-${shortId}` },
    ...(query ? { query } : {})
  };
}

export function buildLocalizedTitlePath({
  type,
  shortId,
  slugs,
  locale,
  query
}: {
  type: 'movie' | 'tv';
  shortId: string;
  slugs: TitleSlug[] | undefined;
  locale: AppLocale;
  query?: RouteQuery;
}): string {
  const href = buildTitleRouteHref({ type, shortId, slugs, locale, query });

  return buildLocalizedPath({
    locale,
    route: href.pathname,
    params: href.params,
    query: href.query
  });
}

export function buildLanguageAlternates({
  baseUrl,
  route,
  paramsForLocale,
  query,
  locales = SUPPORTED_LOCALES
}: {
  baseUrl: string;
  route: LocalizedRoute;
  paramsForLocale?: (locale: AppLocale) => RouteParams | undefined;
  query?: RouteQuery;
  locales?: readonly AppLocale[];
}): Record<string, string> {
  const languages = Object.fromEntries(
    locales.map(locale => [
      REGION_HREFLANG[locale],
      buildLocalizedUrl({
        baseUrl,
        locale,
        route,
        params: paramsForLocale?.(locale),
        query
      })
    ])
  );

  return {
    'x-default': buildLocalizedUrl({
      baseUrl,
      locale: DEFAULT_LOCALE,
      route,
      params: paramsForLocale?.(DEFAULT_LOCALE),
      query
    }),
    ...languages
  };
}

export function buildTitleLanguageAlternates({
  baseUrl,
  type,
  shortId,
  slugs,
  locales = SUPPORTED_LOCALES
}: {
  baseUrl: string;
  type: 'movie' | 'tv';
  shortId: string;
  slugs: TitleSlug[] | undefined;
  locales?: readonly AppLocale[];
}): Record<string, string> {
  const route = type === 'movie' ? '/movie/[id]' : '/tv/[id]';

  return buildLanguageAlternates({
    baseUrl,
    route,
    paramsForLocale: locale => ({
      id: `${getTitleSlug(slugs, locale)?.slug || 'no'}-${shortId}`
    }),
    locales
  });
}
